// Módulo de Usuarios (director): alta por correo + rol, editar rol y activar/desactivar.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createUser, deleteUser, listUsers, updateUser } from '@/services/users'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Badge } from '@/components/ui/Badge'
import { useAuth } from '@/hooks/useAuth'
import { WAREHOUSES } from '@/services/deliveries'
import type { Role, User } from '@/types'

const ROLES: Role[] = ['admin', 'almacen', 'repartidor', 'vendedor']
const ROLE_LABEL: Record<Role, string> = {
  admin: 'Administrador',
  almacen: 'Almacén',
  repartidor: 'Repartidor',
  vendedor: 'Vendedor',
}
const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm shadow-sm transition ' +
  'focus:border-ds-500 focus:outline-none focus:ring-2 focus:ring-ds-500/20'

export default function UsersList() {
  const qc = useQueryClient()
  const { user: me } = useAuth()
  const { data, isLoading } = useQuery({ queryKey: ['users'], queryFn: () => listUsers() })
  const [form, setForm] = useState({ name: '', email: '', role: 'repartidor' as Role, warehouse: '' })
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] })

  const create = useMutation({
    mutationFn: () => createUser(form),
    onSuccess: () => {
      setForm({ name: '', email: '', role: 'repartidor', warehouse: '' })
      setError(null)
      invalidate()
    },
    onError: (e: unknown) => {
      const msg = (e as { message?: string })?.message
      setError(msg ?? 'No se pudo crear el usuario.')
    },
  })

  const update = useMutation({
    mutationFn: (v: { id: string; changes: Partial<User> }) => updateUser(v.id, v.changes),
    onSuccess: invalidate,
    onError: (e: unknown) =>
      window.alert((e as { message?: string })?.message ?? 'No se pudo guardar el cambio. Revisa tu conexión e intenta de nuevo.'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError: (e: unknown) => {
      const msg = (e as { message?: string })?.message
      setError(msg ?? 'No se pudo eliminar el usuario.')
    },
  })

  // Activos primero (alfabético), inactivos al final y atenuados.
  const sorted = [...(data?.items ?? [])].sort(
    (a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name),
  )

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Usuarios"
        subtitle="Da de alta a tu equipo por correo; se vincula con su cuenta de Google en su primer inicio de sesión. Los vendedores cotizan con el vendedor de SAP que tenga su mismo correo."
      />

      {/* Alta */}
      <Card className="mb-6 p-4">
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Nuevo usuario</h2>
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <input
            className={inputCls}
            placeholder="Nombre"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            className={inputCls}
            type="email"
            placeholder="correo@hotel-depot.com"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <select
            className={inputCls}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
          {(form.role === 'almacen' || form.role === 'vendedor') && (
            <select
              className={inputCls}
              value={form.warehouse}
              onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
            >
              <option value="">{form.role === 'vendedor' ? 'Almacén por omisión: —' : 'Todos los almacenes'}</option>
              {WAREHOUSES.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          )}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Agregando…' : 'Agregar'}
          </Button>
        </form>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </Card>

      {/* Tabla */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3">Nombre</th>
                <th className="p-3">Correo</th>
                <th className="p-3">Rol</th>
                <th className="p-3">Almacén</th>
                <th className="p-3">Estado</th>
                <th className="p-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-400">
                    Cargando…
                  </td>
                </tr>
              )}
              {sorted.map((u) => (
                <tr key={u.id} className={`transition hover:bg-gray-50/60 ${u.is_active ? '' : 'opacity-60'}`}>
                  <td className="p-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {u.name}
                      {!u.google_sub && <Badge variant="amber">pendiente 1er login</Badge>}
                    </div>
                  </td>
                  <td className="p-3 text-gray-600">{u.email}</td>
                  <td className="p-3">
                    <select
                      className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-ds-500 focus:outline-none focus:ring-2 focus:ring-ds-500/20"
                      value={u.role}
                      onChange={(e) => {
                        const newRole = e.target.value as Role
                        // Auto-degradarse te saca de este módulo: pide confirmación.
                        if (
                          u.id === me?.id &&
                          newRole !== 'admin' &&
                          !window.confirm('Vas a cambiar TU PROPIO rol: perderás acceso a este módulo de Usuarios y no podrás revertirlo tú mismo. ¿Continuar?')
                        )
                          return
                        update.mutate({ id: u.id, changes: { role: newRole } })
                      }}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3">
                    {/* Almacén: acota al gerente; para el vendedor es su almacén por omisión al cotizar. */}
                    {u.role === 'almacen' || u.role === 'vendedor' ? (
                      <select
                        className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-ds-500 focus:outline-none focus:ring-2 focus:ring-ds-500/20"
                        value={u.warehouse ?? ''}
                        onChange={(e) =>
                          update.mutate({ id: u.id, changes: { warehouse: e.target.value || null } })
                        }
                      >
                        <option value="">— todos —</option>
                        {WAREHOUSES.map((w) => (
                          <option key={w} value={w}>{w}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-400">n/a</span>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge variant={u.is_active ? 'green' : 'gray'}>
                      {u.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => update.mutate({ id: u.id, changes: { is_active: !u.is_active } })}
                      className="text-sm font-medium text-ds-700 hover:underline"
                    >
                      {u.is_active ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`¿Eliminar a ${u.name}? Solo es posible si no tiene actividad registrada (si la tiene, desactívalo).`))
                          remove.mutate(u.id)
                      }}
                      disabled={remove.isPending}
                      className="ml-3 text-sm font-medium text-red-600 hover:underline disabled:opacity-50"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {data && data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-400">
                    Sin usuarios.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
